import React, { FC } from 'react'
import { Button } from 'src/components/ui'
import VerificationBadge from '../verification-badge/VerificationBadge'
import { useTranslation } from 'react-i18next'
import { useApprovePropertyMutation, useWithdrawPropertyMutation } from 'src/redux/api'
import InitialsAvatar from 'src/components/elements/initials-avatar/InitialsAvatar'

export interface IRequest {
  id: string
  propertyId: string
  userId: string
  avatar: string
  name: string
  isVerified?: boolean
  totalRequests: number
  activeRequests: number
  tags: string[]
}

export interface IRequestCard {
  request: IRequest
  propertyId?: string
  // true when the card is rendered in the "responded" view — the action
  // becomes withdrawing the response instead of making one (roadmap 14.3)
  responded?: boolean
}

const RequestCard: FC<IRequestCard> = ({ request, propertyId, responded }) => {
  const { t } = useTranslation()
  const [approveProperty, { isLoading }] = useApprovePropertyMutation()
  const [withdrawProperty, { isLoading: isWithdrawing }] = useWithdrawPropertyMutation()

  const translateTag = (tag: string): string => {
    if (!tag) return tag

    // PropertyType.* tags (backend translation failed)
    if (tag.includes('PropertyType.')) {
      const type = tag.replace('PropertyType.', '').toUpperCase()
      const typeMap: Record<string, string> = {
        APARTMENT: t('step1.apartment'),
        HOUSE: t('step1.house'),
        TOWNHOUSE: t('step1.townhouse'),
        DUPLEX: t('step1.duplex'),
        PART_OF_HOUSE: t('step1.partOfHouse'),
        ROOM: t('step1.room'),
        COMMERCIAL_PROPERTY: t('step1.commercialProperty'),
        PLOT: t('step1.plot'),
        GARAGE: t('step1.garage'),
      }
      return typeMap[type] || tag
    }

    // Room count tags like "1-5+ propertyController.rooms" or "1-5 propertyController.rooms"
    if (tag.includes('propertyController.rooms')) {
      const roomPart = tag.replace('propertyController.rooms', '').trim()
      return `${roomPart} ${t('step3.rooms')}`
    }

    // Default: return as is (already translated by backend or plain text like price)
    return tag
  }

  const handleApproveProperty = async () => {
    const actualPropertyId = request.propertyId || propertyId
    if (!actualPropertyId) {
      return
    }
    await approveProperty({ propertyId: actualPropertyId, propertySearchId: request.id }).unwrap()
  }

  const handleWithdrawProperty = async () => {
    const actualPropertyId = request.propertyId || propertyId
    if (!actualPropertyId) {
      return
    }
    await withdrawProperty({ propertyId: actualPropertyId, propertySearchId: request.id }).unwrap()
  }

  return (
    <div className="flex overflow-hidden flex-col bg-surface rounded-2xl shadow-[0rem_.25rem_1.5rem_rgba(0,0,0,0.09)]">
      <div className="flex flex-col gap-2 p-2 px-4 bg-inverse text-white">
        <div className="flex overflow-hidden relative gap-4 justify-center items-center pl-1.5  w-full rounded-lg font-inter">
          <div className="w-[3.125rem] h-[3.125rem] relative">
            <div className="size-full rounded-full overflow-hidden">
              {/* No presence data reaches this card, so no "online" dot —
                  it used to be green for everyone. */}
              <InitialsAvatar src={request.avatar} name={request.name} />
            </div>
          </div>
          <div className="flex flex-col gap-1 text-sm flex-1">
            <div className="font-semibold">{request.name}</div>
            <VerificationBadge
              className="!px-2 !py-1 !rounded !w-auto !self-start gap-2"
              verified={request.isVerified}
            />
          </div>
        </div>
        <div className="grid py-0.5 grid-cols-3 gap-2 w-full">
          {/* <div className="flex gap-0.5 flex-col">
            <div className="text-sm max-w-[3.125rem]">{t('request.timeOnSite')}</div>
            <div className="self-start text-base font-black leading-tight">
              {request.yearsOnSite} роки
            </div>
          </div> */}
          <div className="flex gap-0.5 flex-col">
            <div className="text-sm max-w-[3.125rem]">{t('request.numberOfRequests')}</div>
            <div className="self-start text-base font-black leading-tight">
              {request.totalRequests}
            </div>
          </div>
          <div className="flex gap-0.5 flex-col">
            <div className="text-sm max-w-[3.125rem]">{t('request.activeRequests')}</div>
            <div className="self-start text-base font-black leading-tight">
              {request.activeRequests}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2 py-2">
        <ul className="flex flex-wrap gap-1 justify-center items-center px-2 w-full text-sm text-center text-ink">
          {request.tags.map((tag, index) => (
            <li key={index} className="px-4 py-2 my-auto grow bg-brand-soft rounded-2xl font-inter">
              {translateTag(tag)}
            </li>
          ))}
        </ul>
        <div className="w-full bg-ink bg-opacity-10 h-px" />
        {/* The bin button that used to sit here was dead — no handler and no
            API behind it; a no-op control on every card only misleads (39.4) */}
        <div className="flex gap-2 items-start px-2 w-full">
          {responded ? (
            <Button
              onClick={handleWithdrawProperty}
              disabled={isWithdrawing}
              isLoading={isWithdrawing}
              className="flex-1 !h-11 md:!h-8 !py-0.5 !border-danger !text-danger hover:!bg-danger-strong hover:!text-white"
              variant={'outlined'}
              iconClassName="px-1 h-[1.5rem] !w-auto min-w-[1.5rem] text-center text-sm bg-brand-soft rounded-[2.75rem] text-ink transition-all group-hover/button:bg-surface"
            >
              {t('request.withdrawOffer')}
            </Button>
          ) : (
            <Button
              onClick={handleApproveProperty}
              disabled={isLoading}
              isLoading={isLoading}
              className="flex-1 !h-11 md:!h-8 !py-0.5"
              variant={'outlined'}
              iconClassName="px-1 h-[1.5rem] !w-auto min-w-[1.5rem] text-center text-sm bg-brand-soft rounded-[2.75rem] text-ink transition-all group-hover/button:bg-surface"
            >
              {t('request.makeOffer')}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default RequestCard
